import { useLanguage } from "../contexts/LanguageContext";

interface TimeAgoTranslations {
  justNow: string;
  minuteAgo: string;
  minutesAgo: (count: number) => string;
  hourAgo: string;
  hoursAgo: (count: number) => string;
  dayAgo: string;
  daysAgo: (count: number) => string;
}

export const formatTimeAgo = (
  dateString: string | undefined,
  translations: TimeAgoTranslations,
  locale: string = "en-US"
): string => {
  if (!dateString) {
    return "-";
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "-";
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return translations.justNow;
  }

  const minutes = Math.floor(diffInSeconds / 60);
  if (minutes < 60) {
    return minutes === 1
      ? translations.minuteAgo
      : translations.minutesAgo(minutes);
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1
      ? translations.hourAgo
      : translations.hoursAgo(hours);
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return days === 1
      ? translations.dayAgo
      : translations.daysAgo(days);
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const useTimeAgo = () => {
  const { language, t } = useLanguage();

  const locale = language === "ja" ? "ja-JP" : "en-US";

  const getTimeAgo = (dateString: string | undefined): string => {
    return formatTimeAgo(
      dateString,
      {
        justNow: t("timeAgo.justNow"),
        minuteAgo: t("timeAgo.minuteAgo"),
        minutesAgo: (count: number) =>
          t("timeAgo.minutesAgo", { count }),
        hourAgo: t("timeAgo.hourAgo"),
        hoursAgo: (count: number) =>
          t("timeAgo.hoursAgo", { count }),
        dayAgo: t("timeAgo.dayAgo"),
        daysAgo: (count: number) =>
          t("timeAgo.daysAgo", { count }),
      },
      locale
    );
  };

  return { getTimeAgo };
};

